import { FormEventHandler, useEffect, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FieldErrors,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { Toast } from "@sscs/ui";
import { useGetWhoamiQuery } from "Services";
import { apiErrorToast, limitChars } from "Utils";
import * as yup from "Utils/yup";
import { useUpdateUserMutation } from "../services/userSettings.api";

type UserProfileFormData = {
  firstName: string;
  lastName: string;
  email: string;
};

export const UserProfileFormSchema: yup.SchemaOf<UserProfileFormData> =
  yup.object({
    firstName: limitChars().required("First name is required"),
    lastName: limitChars().required("Last name is required"),
    email: yup.string().required("Email is required"),
  });

type UseUserProfileForm = {
  errors: FieldErrors<UserProfileFormData>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  formMethods: UseFormReturn<UserProfileFormData>;
};

const useUserProfileForm = (): UseUserProfileForm => {
  const { data: whoami } = useGetWhoamiQuery();
  const [updateUser] = useUpdateUserMutation();

  const defaultValues = useMemo(
    () => ({
      firstName: whoami?.first_name,
      lastName: whoami?.last_name,
      email: whoami?.email,
    }),
    [whoami?.first_name, whoami?.last_name, whoami?.email],
  );

  const formMethods = useForm<UserProfileFormData>({
    resolver: yupResolver(UserProfileFormSchema),
    defaultValues,
  });

  const {
    formState: { errors, isSubmitSuccessful },
    handleSubmit,
    reset,
  } = formMethods;

  // to reset after a submit, react-hook-form recommend doing so inside a useEffect
  // https://react-hook-form.com/api/useform/reset/
  useEffect(() => {
    reset(defaultValues);
  }, [isSubmitSuccessful, reset, defaultValues]);

  const onSubmit: SubmitHandler<UserProfileFormData> = async (data) => {
    try {
      if (whoami) {
        await updateUser({
          userId: whoami.user_id,
          body: {
            first_name: data.firstName,
            last_name: data.lastName,
          },
        }).unwrap();
        Toast.show({
          title: "User profile updated",
          type: "success",
        });
      }
    } catch (error) {
      apiErrorToast(error, "Could not update user profile");
    }
  };

  return {
    onSubmit: handleSubmit(onSubmit),
    errors,
    formMethods,
  };
};

export default useUserProfileForm;
